import { computed, ref, watch } from 'vue';
import { defineStore } from 'pinia';
import { SignalRRoom } from '../types/signalr';
import { QuizAudioKey, QuizCurrentQuestion, QuizProps, QuizSetting, QuizState } from '../types/quiz';
import { useSignalRStore } from './signalr';
import { useClassStore, useSessionStore, useUserStore } from '.';
import { Session } from '../types/session';
import { AttendStatus, ClassStudentInfo } from '../types/class';
import { AssessmentAnswer } from '../types/assessment';
import { UserRole } from '../types/user';
import { audioManager, audioOptions } from '../utils/audioManager';

export const useQuizStore = defineStore('quiz', () => {
    const signalr = useSignalRStore()
    const sessionStore = useSessionStore()
    const classStore = useClassStore()
    const userStore = useUserStore()

    // Computed property for the quiz object
    const quiz = computed<QuizProps>(() => {
        const sessionQuiz = sessionStore.classContent?.[Session.quiz];
        if (typeof sessionQuiz === 'object') {
            return sessionQuiz as QuizProps;
        }
        // Default quiz object
        return {
            id: 0,
            name: '',
            questions: [],
            totalPoint: 0,
            totalTime: 0,
            url: '',
            scormUrl: ''
        };
    });

    // Reactive state variables
    const quizState = ref<QuizState>(QuizState.NOT_STARTED);
    const questionState = ref<QuizCurrentQuestion>();
    const previewQuiz = ref<boolean>(false);
    const isCountingDown = ref<boolean>(false);
    const showStudentPoint = ref<boolean>(true);
    const leaderBoard = ref<ClassStudentInfo[]>([]);
    const darkMode = ref<boolean>(true);
    const bgmVolumeState = ref<audioOptions>(audioManager.getVolume(QuizAudioKey.BGM))

    // Reactive quiz settings
    const quizSetting = ref<QuizSetting>({
        showCorrectAnswer: 0,
        showRanking: 0,
        questionTime: quiz.value?.totalTime || 0,
    });

    // Computed property for the current question index
    const curentQuestionIndex = computed<number>(() => {
        return questionIndexById(questionState.value?.id)
    });

    const studentAnswers = computed<AssessmentAnswer[]>(() => {
        if (userStore.userRole !== UserRole.student) return []
        return classStore.currentStudent?.quizQuestion ?? []
    })

    // Reactive state for the remaining time
    const timeRemaining = ref<number>(0);

    // Watch for changes in classStore.viewmode or sessionStore.currentSession
    watch(
        [() => classStore.viewMode, () => sessionStore.currentSession],
        () => {
            if (quizState.value === QuizState.ON_GOING && questionState.value) {
                const currentTime = Math.floor(Date.now() / 1000); // Current Unix timestamp
                const elapsedTime = currentTime - questionState.value.startTime;
                timeRemaining.value = Math.max(questionState.value.totalTime - elapsedTime, 0); // Ensure non-negative time
                console.log('Time remaining updated:', timeRemaining.value);
            }
        }
    );

    // Watch for changes in questionState to update timeRemaining
    watch(questionState, (newState) => {
        if (newState?.state === QuizState.ON_GOING) {
            const currentTime = Math.floor(Date.now() / 1000); // Current Unix timestamp
            const elapsedTime = currentTime - newState.startTime;
            timeRemaining.value = Math.max(newState.totalTime - elapsedTime, 0); // Ensure non-negative time
            showStudentPoint.value = true;
        }
    });

    watch(bgmVolumeState.value, (newState: audioOptions) => {
        // newState.muted : boolean|undefined
        audioManager.setMuted(QuizAudioKey.BGM, newState.muted ?? false)
    })

    // Synchronize quiz state with SignalR room data
    function syncQuiz(room: SignalRRoom) {
        quizState.value = room.quiz.state;
        questionState.value = room.quiz.currentQuestion;
        quizSetting.value = {
            showCorrectAnswer: room.quiz.showCorrectAnswer,
            showRanking: room.quiz.showRanking,
            questionTime: room.quiz.questionTime,
        };

    }

    function updateLeaderboard() {
        leaderBoard.value = classStore.selectedClassDetails?.students?.filter(student => student.status === AttendStatus.attended).sort((a, b) => {
            const pointDifference = studentTotalPoint(b) - studentTotalPoint(a);
            return pointDifference !== 0 ? pointDifference : studentAnswerTime(a) - studentAnswerTime(b);
        }) || [];
    }

    // Update quiz state
    function updateQuizState(state: QuizState) {
        signalr.updateQuizState(state);
    }

    // Calculate total answer time for a student
    function studentAnswerTime(student: ClassStudentInfo): number {
        return student.quizQuestion?.reduce((total, answer) => total + (answer.endTime - answer.startTime), 0) ?? 0;
    }

    // Calculate total points for a student
    function studentTotalPoint(student: ClassStudentInfo): number {
        const point = student.quizQuestion?.reduce(
            (total, studentAnswer) => {
                if (studentAnswer.answer.every((ans) => ans.isCorrect === 1) && studentAnswer.answer.length === studentAnswer.correctAnswers.length) {
                    return total + studentAnswer.answer.reduce((subTotal, answer) => subTotal + answer.point, 0)
                }
                return total + 0
            },
            0
        ) ?? 0;
        // const point = student.quizQuestion?.filter((studentAnswer)=>{
        //     studentAnswer
        // })
        return parseFloat(point.toFixed(2));
    }

    // Calculate property for the current question index
    function questionIndexById(id: number | undefined): number {
        return quiz.value.questions.findIndex((question) => question.id === id);
    };

    return {
        quiz,
        quizState,
        previewQuiz,
        quizSetting,
        questionState,
        isCountingDown,
        showStudentPoint,
        timeRemaining,
        leaderBoard,
        studentAnswers,
        curentQuestionIndex,
        darkMode,
        bgmVolumeState,
        syncQuiz,
        questionIndexById,
        updateQuizState,
        studentAnswerTime,
        studentTotalPoint,
        updateLeaderboard,
    };
});